import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Label } from '@wf/sw-ui-kit';

import { LicenseField } from '../LicenseField';

import { NamesAndPartiesTabFields } from '../../types/fieldEnums';
import { PHONE_REGEXP } from '../../../../constants/regex';

import { type ICompanyRimmEntity, type TRimmEntityOption } from '../../../rimm/data/rimmEntityTypes';
import { RimmEntityType } from '../../../rimm/data/rimmEntityEnums';

import styles from './NamesAndParties.module.scss';

interface ICompanyInfoFieldsProps {
  disabled: boolean;
  companyOptions: TRimmEntityOption[RimmEntityType.company][] | null;
}

function CompanyInfoFields({ disabled, companyOptions }: ICompanyInfoFieldsProps) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const onCompanyCodeChange = (value: (ICompanyRimmEntity & SelectOption) | null) => {
    const address1: string = value?.address1 ?? '';
    const address2: string = value?.address2 ?? '';
    setValue(NamesAndPartiesTabFields.companyAddress, address1 || address2 ? `${address1} ${address2}` : '');
    setValue(NamesAndPartiesTabFields.companyEmail, value?.email || '');
    setValue(NamesAndPartiesTabFields.companyPhone, value?.phoneNumber || '');
  };

  return (
    <>
      {companyOptions ? (
        <LicenseField.SelectField
          label={
            <Label
              helpText={t(
                'Unique identifier and name of the company carrying out transactions for imports or exports of goods'
              )}
            >
              {t('Company Code and Name')}
            </Label>
          }
          onChange={(value) => onCompanyCodeChange(value as (ICompanyRimmEntity & SelectOption) | null)}
          configName={NamesAndPartiesTabFields.companyCode}
          placeholder={t('Search Company Code and Name')}
          name={NamesAndPartiesTabFields.companyCode}
          className={styles.twoCellField}
          options={companyOptions}
          isClearable
        />
      ) : (
        <LicenseField.RimmSelectField
          label={
            <Label
              helpText={t(
                'Unique identifier and name of the company carrying out transactions for imports or exports of goods'
              )}
            >
              {t('Company Code and Name')}
            </Label>
          }
          onChange={(value) => onCompanyCodeChange(value as (ICompanyRimmEntity & SelectOption) | null)}
          configName={NamesAndPartiesTabFields.companyCode}
          placeholder={t('Search Company Code and Name')}
          name={NamesAndPartiesTabFields.companyCode}
          className={styles.twoCellField}
          entity={RimmEntityType.company}
          minSuggest={2}
          withTag
          {...(disabled ? { disabled: true } : undefined)}
        />
      )}

      <LicenseField.TextField
        label={
          <Label helpText={t('Address of the company carrying out transactions for imports or exports of goods.')}>
            {t('Company Address')}
          </Label>
        }
        name={NamesAndPartiesTabFields.companyAddress}
        placeholder={t('Company Address')}
        className={styles.twoCellField}
        textarea
        disabled
      />
      <LicenseField.TextField
        label={
          <Label
            helpText={t(
              'Contact phone number of the company carrying out transactions for imports or exports of goods.'
            )}
          >
            {t('Company Phone')}
          </Label>
        }
        configName={NamesAndPartiesTabFields.companyPhone}
        name={NamesAndPartiesTabFields.companyPhone}
        placeholder={t('Write company phone')}
        className={styles.twoCellField}
        regexp={PHONE_REGEXP}
      />
      <LicenseField.TextField
        label={
          <Label
            helpText={t('Email address of the company carrying out transactions for imports or exports of goods.')}
          >
            {t('Company Email')}
          </Label>
        }
        configName={NamesAndPartiesTabFields.companyEmail}
        name={NamesAndPartiesTabFields.companyEmail}
        placeholder={t('Write company email')}
        className={styles.twoCellField}
      />
    </>
  );
}

export default CompanyInfoFields;
